/* eslint-disable max-statements-per-line */
import type { CurvePath, Group, Object3D, SceneControl, Vector3 } from '@anov/3d-core'
import { Box3, LoaderModule, utils } from '@anov/3d-core'
import { BaseTraffic } from '../base'
import type { RealismTrafficInterface } from '.'

const { moveWithLine } = utils

type ResourceParams = {
  url: string
}

type LanelineControl = {
  index: number
  direction?: number
}

type RealismTrafficParams = {
  path: CurvePath<Vector3>
  width?: number
  divide?: number
  resource: ResourceParams[]
  baseTime?: number
  totalNum?: number
  control?: LanelineControl[]
  corner?: number
}

const loaderModule = new LoaderModule()

const overrideVisible = (target: any, propertyKey: string | symbol) => {
  const getter = () => {
    return target._visible
  }

  const setter = (newValue: any) => {
    target._visible = newValue

    if (newValue === false) {
      target._control ?? [].forEach((control) => {
        control.stop()
      })
    }
    else {
      target._control ?? [].forEach((control) => {
        control.continue()
      })
    }
  }

  Object.defineProperty(target, propertyKey, {
    get: getter,
    set: setter,
    enumerable: true,
    configurable: true,
  })
}

/**
 * 写实风格车辆交通组件
 */
export class RealismTraffic extends BaseTraffic implements RealismTrafficInterface {
  /** 车辆模型池 */
  private _resource: ResourceParams[]

  /** 基准时间 */
  private _baseTime: number

  /** 车辆数量 */
  private _totalNum = 5

  private _startingPoints: Vector3[] = []

  private _startingLookatPoints: Vector3[] = []

  private _startingProgressPoints: number[] = []

  private _startinglanelinePoints: number[] = []

  private _visible = true

  private _control: ReturnType<typeof moveWithLine>[] = []

  private _lanelineControl: LanelineControl[] = []

  /** 基准载具池 */
  private _benchmarkVehicles: Group[] = []

  /** 实际载具 */
  private _vehicles: Group[] = []

  /** 车道线 */
  public lanelines: CurvePath<Vector3>[] = []

  public get width(): number {
    return this._width
  }

  public set width(w: number) {
    this._width = w

    this.generateLaneLines()
    this.loadBenchmarkModel()
  }

  public get divide(): number {
    return this._divide
  }

  public set divide(num: number) {
    this._divide = num

    this.generateLaneLines()
    this.loadBenchmarkModel()
  }

  @overrideVisible
  override visible = true

  public get baseTime(): number {
    return this._baseTime
  }

  constructor(params: RealismTrafficParams, sceneControl: SceneControl) {
    super(params, sceneControl)

    const { path, width = 2, divide = 1, resource, totalNum = 5, baseTime = 10, control = [], corner = 10 } = params

    this._path = path
    this._width = width
    this._divide = divide
    this._resource = resource
    this._totalNum = totalNum
    this._baseTime = baseTime
    this._lanelineControl = control
    this._corner = corner

    if (width === 0 || divide === 0) {
      console.error('宽度或车道数不能为0')
      throw new Error('宽度或车道数不能为0')
    }

    this.generateLaneLines()
    this.loadBenchmarkModel()
    this.handleVisibilityChange()
  }

  /**
   * load benchmark model
   */
  private loadBenchmarkModel() {
    this._benchmarkVehicles = []

    if (!this._resource.length)
      return

    const promises = this._resource.map(({ url }) => {
      return loaderModule.loadGLTF({
        url,
        draco: true,
        // todo sdk 接口支持 decoderPath
        dracoPath: this.sceneControl?.context.storeManagement.get('decoderPath') ?? '/draco/',
      })
    })

    Promise.allSettled(promises).then((res) => {
      res.forEach((modelRes) => {
        if (modelRes.status === 'fulfilled')
          this._benchmarkVehicles.push(this.normalizedVehicleSize(modelRes.value.scene as Group))
      })

      const randomPool = this.generateRandomPool()
      this.generateInitPosition(randomPool)
    })
  }

  /**
   * get max vehicle length
   */
  private getMaxVehicleLength() {
    const maxLength = this._benchmarkVehicles.reduce((max, vehicle) => {
      const box = new Box3().setFromObject(vehicle)
      const currentLength = box.max.z - box.min.z

      return currentLength > max ? currentLength : max
    }, 0)

    return maxLength
  }

  /**
   * check reasonable location
   */
  private generateRandomPool() {
    const lineLengths = this.lanelines.map(line => line.getLength())
    const part = lineLengths.map(length => Math.floor(length / this.getMaxVehicleLength()))
    const capacity = part.reduce((pre, next) => { return pre + next }, 0)

    if (capacity < this._totalNum)
      throw new Error('载具数量不合理')

    return this.buildRandomPool(part)
  }

  /**
   * build random pool
   */
  private buildRandomPool(part: number[]) {
    const pool = []

    part.forEach((part, i) => {
      for (let j = 0; j < part; j++) {
        pool.push({
          t: 1 / part * j,
          line: i,
        })
      }
    })

    return pool
  }

  /**
   * handle requestAnimationFrame pause
   */
  private handleVisibilityChange() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._control.forEach((control) => {
          control.stop()
        })
      }
      else {
        this._control.forEach((control) => {
          control.continue()
        })
      }
    })
  }

  /**
   * generate random movement starting point
   */
  private generateInitPosition(randomPool) {
    for (let i = 0; i < this._totalNum; i++) {
      const index = Math.floor(Math.random() * randomPool.length)
      const current = randomPool[index]
      const currentLaneline = this.lanelines[current.line]
      const point = currentLaneline.getPointAt(current.t)

      this._startingPoints.push(point)
      this._startingLookatPoints.push(currentLaneline.getPointAt((current.t + 0.01) % 1))
      this._startingProgressPoints.push(current.t)
      this._startinglanelinePoints.push(current.line)

      randomPool.splice(index, 1)
    }

    this.placeVehicle()
  }

  /**
   * place vehicle
   */
  private placeVehicle() {
    this._vehicles.forEach((vehicle, i) => {
      vehicle.removeFromParent()
      this._control[i].remove()
    })

    this._vehicles = []
    this._control = []

    for (let i = 0; i < this._startingPoints.length; i++) {
      const randomModelIndex = Math.floor(Math.random() * this._benchmarkVehicles.length)
      const currentVehicle = this._benchmarkVehicles[randomModelIndex].clone()

      currentVehicle.position.copy(this._startingPoints[i])
      currentVehicle.lookAt(this._startingLookatPoints[i])

      this._vehicles.push(currentVehicle)
      this.add(currentVehicle)
      this.run(this.lanelines[this._startinglanelinePoints[i]], currentVehicle, this._startingProgressPoints[i], this._startinglanelinePoints[i])
    }

    this.dispatchEvent({
      type: 'realismTraffic-loaded',
    })
  }

  /**
   * run
   * @param laneline
   * @param model
   * @param progress
   * @param laneIndex
   */
  private run(laneline: CurvePath<Vector3>, model: Object3D, progress: number, laneIndex: number) {
    const lc = this._lanelineControl.find(lc => lc.index === laneIndex)

    if (this.baseTime === 0) {
      console.log('baseTime is 0')
      return
    }

    const control = moveWithLine(model, laneline, {
      duration: this.baseTime,
      direction: (lc && lc?.direction === 0) ? -1 : 1,
      sceneControl: this.sceneControl,
    })

    this._control.push(control)
    control.setProgress(progress)
  }

  /**
   * normalized vehicle size
   */
  private normalizedVehicleSize(model: Group) {
    const box = new Box3().setFromObject(model)
    const currentWidth = box.max.x - box.min.x
    const scalingFactor = (this._width / (this._divide + 1)) / currentWidth * 0.7

    model.scale.set(scalingFactor, scalingFactor, scalingFactor)

    return model
  }

  override removeFromParent(): this {
    // todo: 升级three.js后，父对象变化，会触发此函数，重组内部引起关系
    this.parent && this._control.forEach((control) => {
      control.remove()
    })

    return super.removeFromParent()
  }
}